/**
 * Susurro Home: device identity (guest-first), conversation list
 * and starting a 1:1 chat or a group. All E2E over
 * `@susurro/protocol` + the blind relay of `apps/signaling`.
 */

import { createSignal, For, Show } from "solid-js";
import { createRoute, Link, useNavigate } from "@tanstack/solid-router";
import {
  ChevronRight,
  Copy,
  Lock,
  MessageSquarePlus,
  QrCode as QrCodeIcon,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Type,
  UserRound,
  Users,
  Vault,
} from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { rootRoute } from "@/routes/__root";
import { useChat } from "@/providers/chat-provider";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Atmosphere } from "@/components/ui/atmosphere";
import { Wordmark } from "@/components/brand/wordmark";
import { QrCode } from "@/components/ui/qr-code";
import { isQrScanSupported, QrScanner } from "@/components/chat/qr-scanner";
import { buildPairingCode, parsePairingCode } from "@/lib/pairing-code";
import { canCreateGroupOf, entitlementsFor, getPlan } from "@/lib/monetization/entitlements";

function HomeComponent() {
  const chat = useChat();
  const { t } = useI18n();
  const navigate = useNavigate();

  const membersLabel = (count: number) => t(msg`${count} members`);

  const [peerId, setPeerId] = createSignal("");
  const [groupTitle, setGroupTitle] = createSignal("");
  const [groupMembers, setGroupMembers] = createSignal("");
  const [linkTarget, setLinkTarget] = createSignal("");
  const [copied, setCopied] = createSignal(false);
  const [mode, setMode] = createSignal<"none" | "direct" | "group" | "link">("none");
  const [showMyQr, setShowMyQr] = createSignal(false);
  const [scanning, setScanning] = createSignal(false);
  // Monetization framework gate (flags, NO ads): maximum group size.
  const [groupLimitHit, setGroupLimitHit] = createSignal(false);

  const myChatCode = () => buildPairingCode({ kind: "chat", deviceId: chat.deviceId() });
  const myLinkCode = () => buildPairingCode({ kind: "link", deviceId: chat.deviceId() });

  const copyDeviceId = async () => {
    const id = chat.deviceId();
    if (!id) {
      return;
    }
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable — the <code> remains select-all */
    }
  };

  const linkDevice = async () => {
    // Accepts a bare id or a scanned/pasted linking code.
    const id = parsePairingCode(linkTarget())?.deviceId ?? linkTarget().trim();
    if (!id) {
      return;
    }
    await chat.linkDevice(id);
    setLinkTarget("");
    setMode("none");
  };

  const startDirect = async () => {
    // Accepts a bare id or a scanned/pasted chat code (QR).
    const id = parsePairingCode(peerId())?.deviceId ?? peerId().trim();
    if (!id) {
      return;
    }
    const conv = await chat.startDirect(id);
    setPeerId("");
    setMode("none");
    void navigate({
      to: "/c/$conversationId",
      params: { conversationId: conv.id },
    });
  };

  /** Camera scanner result: routes according to the code's purpose. */
  const onScan = (value: string) => {
    setScanning(false);
    const parsed = parsePairingCode(value);
    if (!parsed) {
      return;
    }
    if (parsed.kind === "link") {
      setLinkTarget(parsed.deviceId);
      setMode("link");
      void linkDevice();
    } else {
      setPeerId(parsed.deviceId);
      void startDirect();
    }
  };

  const startGroup = async () => {
    const title = groupTitle().trim() || t(msg`Group`);
    const members = groupMembers()
      .split(/[\s,]+/)
      .map((m) => m.trim())
      .filter(Boolean);
    if (members.length === 0) {
      return;
    }
    // Plan capacity gate (members + you). Product, not security.
    if (!canCreateGroupOf(members.length + 1, getPlan())) {
      setGroupLimitHit(true);
      return;
    }
    setGroupLimitHit(false);
    const conv = await chat.createGroup(title, members);
    setGroupTitle("");
    setGroupMembers("");
    setMode("none");
    void navigate({
      to: "/c/$conversationId",
      params: { conversationId: conv.id },
    });
  };

  const fieldClass =
    "rounded-xl border border-[var(--field-border)] bg-[var(--field-background)] px-3.5 py-2.5 text-sm text-foreground placeholder:text-[var(--field-placeholder)] outline-none transition-shadow focus:ring-2 focus:ring-ring/60";

  // Flat locals so that lingui extracts the message (settings-fonts pattern).
  const planLimitNotice = () => {
    const max = entitlementsFor(getPlan()).maxGroupMembers;
    return t(
      msg`Your plan allows groups of up to ${max} members. Plus unlocks large groups — no ads, ever.`,
    );
  };

  return (
    <div class="relative min-h-dvh w-full">
      <Atmosphere />
      <Button
        variant="ghost"
        size="icon"
        class="absolute right-24 top-4 z-10"
        data-testid="open-backup-settings"
        aria-label={t(msg`Encrypted backup`)}
        onClick={() => void navigate({ to: "/settings/backup" })}
      >
        <Vault class="h-5 w-5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="absolute right-14 top-4 z-10"
        data-testid="open-account-settings"
        aria-label={t(msg`Account`)}
        onClick={() => void navigate({ to: "/settings/account" })}
      >
        <UserRound class="h-5 w-5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="absolute right-4 top-4 z-10"
        data-testid="open-font-settings"
        aria-label={t(msg`Typeface settings`)}
        onClick={() => void navigate({ to: "/settings/fonts" })}
      >
        <Type class="h-5 w-5" />
      </Button>
      <div class="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 p-4 sm:p-6">
        <header class="flex flex-col items-center gap-3 pt-6 pb-2 text-center">
          <Link
            to="/"
            class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          >
            <Wordmark markSize={46} />
          </Link>
          <p class="inline-flex items-center gap-2 rounded-full border border-[var(--glass-border)] bg-[var(--glass)] px-3.5 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
            <Lock class="h-3.5 w-3.5 text-success" />
            {t(msg`Private end-to-end encrypted chat`)}
          </p>
        </header>

        <GlassSurface class="flex flex-col gap-2.5 p-4">
          <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            {t(msg`Your device ID`)}
          </span>
          <div class="flex items-center gap-2">
            <code
              data-testid="device-id"
              class="min-w-0 flex-1 truncate rounded-lg bg-[var(--field-background)] px-3 py-2.5 font-mono text-sm text-foreground select-all"
            >
              <Show when={chat.deviceId()} fallback={t(msg`Starting…`)}>
                {chat.deviceId()}
              </Show>
            </code>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t(msg`Copy`)}
              onClick={() => void copyDeviceId()}
            >
              <Show when={copied()} fallback={<Copy class="h-4 w-4" />}>
                <ShieldCheck class="h-4 w-4 text-success" />
              </Show>
            </Button>
            <Button
              variant={showMyQr() ? "default" : "ghost"}
              size="icon"
              data-testid="show-qr-toggle"
              aria-pressed={showMyQr()}
              aria-label={t(msg`Show QR code`)}
              onClick={() => setShowMyQr(!showMyQr())}
            >
              <QrCodeIcon class="h-4 w-4" />
            </Button>
          </div>
          <p class="text-xs text-muted-foreground">
            {t(msg`Share this ID so others can message you. No account required.`)}
          </p>
          <Show when={showMyQr() && chat.deviceId()}>
            <div class="animate-scale-in flex flex-col items-center gap-2 pt-1">
              <QrCode value={myChatCode()} testid="my-chat-qr" size={200} />
              <p class="text-center text-xs text-muted-foreground">
                {t(msg`Scan this to start an encrypted chat with me.`)}
              </p>
            </div>
          </Show>
        </GlassSurface>

        <div class="flex gap-2">
          <Button
            class="flex-1 gap-2"
            variant={mode() === "direct" ? "default" : "outline"}
            data-testid="new-direct"
            onClick={() => setMode(mode() === "direct" ? "none" : "direct")}
          >
            <MessageSquarePlus class="h-4 w-4" />
            {t(msg`New chat`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            variant={mode() === "group" ? "default" : "outline"}
            data-testid="new-group"
            onClick={() => setMode(mode() === "group" ? "none" : "group")}
          >
            <Users class="h-4 w-4" />
            {t(msg`New group`)}
          </Button>
        </div>

        <Show when={mode() === "direct"}>
          <GlassSurface class="animate-scale-in flex flex-col gap-2 p-4">
            <input
              data-testid="direct-peer-input"
              class={fieldClass}
              placeholder={t(msg`Peer device ID or scanned code`)}
              value={peerId()}
              onInput={(e) => setPeerId(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void startDirect();
                }
              }}
            />
            <div class="flex gap-2">
              <Show when={isQrScanSupported()}>
                <Button
                  variant="outline"
                  class="gap-2"
                  data-testid="scan-qr-button"
                  onClick={() => setScanning(true)}
                >
                  <ScanLine class="h-4 w-4" />
                  {t(msg`Scan QR`)}
                </Button>
              </Show>
              <Button class="flex-1" data-testid="direct-start" onClick={() => void startDirect()}>
                {t(msg`Start chat`)}
              </Button>
            </div>
          </GlassSurface>
        </Show>

        <div class="flex">
          <Button
            class="flex-1 gap-2"
            variant={mode() === "link" ? "default" : "outline"}
            data-testid="link-toggle"
            onClick={() => setMode(mode() === "link" ? "none" : "link")}
          >
            <Smartphone class="h-4 w-4" />
            {t(msg`Link a device`)}
          </Button>
        </div>

        <Show when={mode() === "link"}>
          <GlassSurface class="animate-scale-in flex flex-col gap-3 p-4">
            <div class="flex flex-col gap-2">
              <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                {t(msg`On your new device`)}
              </span>
              <Button
                variant="outline"
                data-testid="link-this-device"
                disabled={chat.linkingEnabled()}
                onClick={() => chat.enableLinking()}
              >
                {t(msg`This is my new device — receive my chats`)}
              </Button>
              <Show when={chat.linkingEnabled()}>
                <p
                  class="flex items-center gap-1.5 text-xs text-muted-foreground"
                  data-testid="link-waiting"
                >
                  <span class="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-success" />
                  {t(msg`Waiting for your other device to send the history…`)}
                </p>
                <div class="flex flex-col items-center gap-2 pt-1">
                  <QrCode value={myLinkCode()} testid="my-link-qr" size={180} />
                  <p class="text-center text-xs text-muted-foreground">
                    {t(msg`Scan this from your primary device to link it.`)}
                  </p>
                </div>
              </Show>
            </div>
            <div class="h-px bg-[var(--separator)]" />
            <div class="flex flex-col gap-2">
              <span class="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                {t(msg`On your primary device`)}
              </span>
              <input
                data-testid="link-target-input"
                class={fieldClass}
                placeholder={t(msg`New device ID`)}
                value={linkTarget()}
                onInput={(e) => setLinkTarget(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void linkDevice();
                  }
                }}
              />
              <div class="flex gap-2">
                <Show when={isQrScanSupported()}>
                  <Button
                    variant="outline"
                    class="gap-2"
                    data-testid="scan-link-qr-button"
                    onClick={() => setScanning(true)}
                  >
                    <ScanLine class="h-4 w-4" />
                    {t(msg`Scan QR`)}
                  </Button>
                </Show>
                <Button class="flex-1" data-testid="link-send" onClick={() => void linkDevice()}>
                  {t(msg`Send my chats to that device`)}
                </Button>
              </div>
            </div>
          </GlassSurface>
        </Show>

        <Show when={mode() === "group"}>
          <GlassSurface class="animate-scale-in flex flex-col gap-2 p-4">
            <input
              data-testid="group-title-input"
              class={fieldClass}
              placeholder={t(msg`Group name`)}
              value={groupTitle()}
              onInput={(e) => setGroupTitle(e.currentTarget.value)}
            />
            <input
              data-testid="group-members-input"
              class={fieldClass}
              placeholder={t(msg`Member device IDs (comma or space separated)`)}
              value={groupMembers()}
              onInput={(e) => setGroupMembers(e.currentTarget.value)}
            />
            <Button data-testid="group-start" onClick={() => void startGroup()}>
              {t(msg`Create group`)}
            </Button>
            <Show when={groupLimitHit()}>
              <p
                role="status"
                data-testid="plan-limit-notice"
                class="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-foreground"
              >
                {planLimitNotice()}
              </p>
            </Show>
          </GlassSurface>
        </Show>

        <section class="flex flex-col gap-2" data-testid="conversation-list">
          <Show
            when={chat.conversations().length > 0}
            fallback={
              <div class="flex flex-col items-center gap-3 py-12 text-center">
                <div class="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--surface-secondary)] text-muted-foreground">
                  <MessageSquarePlus class="h-7 w-7" />
                  <span class="absolute -right-1 -bottom-1 flex h-6 w-6 items-center justify-center rounded-full bg-success text-[var(--success-foreground)]">
                    <Lock class="h-3 w-3" />
                  </span>
                </div>
                <p class="max-w-xs text-sm text-muted-foreground">
                  {t(msg`No conversations yet. Start one above.`)}
                </p>
              </div>
            }
          >
            <For each={chat.conversations()}>
              {(conv) => (
                <button
                  type="button"
                  data-testid="conversation-item"
                  class="group flex w-full items-center gap-3 rounded-2xl border border-[var(--glass-border)] bg-[var(--glass)] px-3.5 py-3 text-left backdrop-blur-sm transition-[transform,background-color] hover:-translate-y-px hover:bg-[var(--surface-secondary)]"
                  onClick={() =>
                    void navigate({
                      to: "/c/$conversationId",
                      params: { conversationId: conv.id },
                    })
                  }
                >
                  <div
                    class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--accent-foreground)]"
                    style={{
                      background:
                        "linear-gradient(135deg, oklch(0.72 0.13 278), oklch(0.66 0.12 210))",
                    }}
                  >
                    <Show
                      when={conv.kind === "group"}
                      fallback={<MessageSquarePlus class="h-5 w-5" />}
                    >
                      <Users class="h-5 w-5" />
                    </Show>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5">
                      <span class="truncate font-semibold text-foreground">{conv.title}</span>
                      <Lock class="h-3 w-3 shrink-0 text-success" />
                    </div>
                    <div class="truncate font-mono text-xs text-muted-foreground">
                      {conv.kind === "group"
                        ? membersLabel(conv.memberDeviceIds.length)
                        : conv.peerDeviceId}
                    </div>
                  </div>
                  <ChevronRight class="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </button>
              )}
            </For>
          </Show>
        </section>
      </div>
      <Show when={scanning()}>
        <QrScanner onResult={onScan} onClose={() => setScanning(false)} />
      </Show>
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeComponent,
});
